import i18n, { loadNamespaces, t } from 'i18next';
import LngDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import HttpApi from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';

import { backendOptions, createMissingKeyRegistrar } from '@/js/libs/i18n-tools';

import { supportedLanguages } from './constants.ts';
import { translationTrackerPostProcessor } from './modules/TranslateMode/index.ts';
import { lowercaseWord } from './utils/string.ts';

enum Formatter {
  LOWERCASE_WORD = 'lowercaseWord',
}

const TRANSLATION_CACHE_ENABLED = import.meta.env.VITE_IS_TRANSLATION_CACHE_ENABLED === 'true';

/**
 * Chained i18next backend with optional LocalStorage caching.
 *
 * When `VITE_IS_TRANSLATION_CACHE_ENABLED` is set, translations are cached in LocalStorage
 * (10 min TTL, invalidated on deploy via COMMITHASH). The creation timestamp is stored
 * alongside the data in the JSON entry, and the backend checks the remaining TTL against it.
 *
 * The backend API endpoint also caches translation responses for 5 minutes per namespace,
 * so even without LocalStorage cache, repeated requests for the same namespace are served from server cache.
 */
const Backend = new ChainedBackend(null, {
  backends: [...(TRANSLATION_CACHE_ENABLED ? [LocalStorageBackend] : []), HttpApi],
  backendOptions: [
    ...(TRANSLATION_CACHE_ENABLED
      ? [
          {
            // reset language cache every 10 minutes
            expirationTime: 10 * 60 * 1000,
            // reset language cache on every deployment
            defaultVersion: window.audienzzGlobals.COMMITHASH,
          },
        ]
      : []),
    backendOptions,
  ],
});

i18n
  .use(LngDetector)
  .use(Backend)
  .use(translationTrackerPostProcessor)
  .use(createMissingKeyRegistrar(i18n))
  .init({
    returnNull: false,
    debug: localStorage.getItem('is-i18n-debug-mod-enabled') === 'true',
    supportedLngs: supportedLanguages,
    fallbackLng: false,
    load: 'currentOnly',
    preload: false,
    keySeparator: false,
    returnEmptyString: false,
    // preload namespaces: messages - used directly in i18n.t calls, backend - the values the backend supplies
    ns: ['backend', 'messages'],
    // the default separator, set explicitly: i18next then splits the namespace off every key, also off a backend value
    // with a space, `,`, `?`, `!` or `;` in it, which it otherwise looks up whole as natural language
    nsSeparator: ':',
    postProcess: ['translationTracker', 'missingKeyRegistrar'],
    // hands `i18nResolved` to post-processors: without it missingKeyRegistrar sees no resolving namespace and registers nothing
    postProcessPassResolved: true,
  });

i18n.services.formatter?.add(Formatter.LOWERCASE_WORD, lowercaseWord);

export const translate = (key: string): Promise<string> => {
  const ns = key.split(':')[0];
  if (!ns) throw new Error(`Invalid key: ${key}`);
  // eslint-disable-next-line rulesdir/key-format -- the caller passes a complete key, whose namespace is loaded first
  return loadNamespaces(ns).then(() => t(key));
};

export default i18n;
